import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';
import { SeatsioSeatingChart } from '@/components/seatsio-seating-chart';
import { useTheme } from '@mui/material/styles';
import { useSeatsIO, useSaturated, useRsvp } from '@/store';
import { extractData } from '@/store/ticket-types/useTicketTypes';
import { fCurrency } from '@/utils/formatNumber';
import useTicketsContext from '@/contexts/tickets/useTicketsContext';
import useEventContext from '@/contexts/event/useEventContext';
import CustomSelectionAlert from '../components/CustomSelectionAlert';
import Instructions from '../components/Instructions';
import Saturated from '@/templates/plus/sections/saturated';
import useEventStatus from '@/contexts/event/useEventStatus';
import useCountdown from '@/hooks/useCountdown';
import useLocales from '@/hooks/useLocales';
import useUserTracking from '@/hooks/userTracking';

const HOLDTOKEN_LIFE_SECONDS = 240;
const HOLDTOKEN_LIFE_MINUTES = 5;
const HOLDTOKEN_LIMIT_TO_EXTEND_SECONDS = 3;

function CheckoutMap({
  sx = {},
  onHold = () => {},
  disabledAlerts = false,
  disabledSaturated = false,
}) {
  const theme = useTheme();
  const { currentLang } = useLocales();
  const [mapInteraction, setMapInteraction] = useState(disabledAlerts || false);
  const {
    onHoldTokenExpired,
    onRenderStarted,
    onObjectSelected,
    onObjectDeselected,
    onHoldSucceeded,
    onReleaseHoldSucceeded,
    onObjectClicked,
    onObjectStatusChanged,
    onHoldFailed,
    onReleaseHoldFailed,
  } = useTicketsContext();

  const router = useRouter();

  const { isActive: isActiveRsvp, seatsAvailables, rsvp } = useRsvp();

  const {
    event: {
      event,
      preferences: { currency },
    },
    ticketTypes,
  } = useEventContext();

  const { selectionAbandoned } = useUserTracking();

  const { categories, availableCategories, maxSelectedTicket } =
    extractData(ticketTypes);

  const {
    seatsIO: { holdToken },
    createHoldTokenAndSave,
    changeExpirationHoldTokenAndSave,
  } = useSeatsIO();

  const { setStartTime, isEnabled } = useSaturated();

  const { eventStatus } = useEventStatus();

  const onHoldHandler = (object, selectedTicketType) => {
    onHoldSucceeded(object, selectedTicketType);
    onHold();
  };

  const onObjectClickedHandler = (args) => {
    onObjectClicked(args);
    if (!mapInteraction) setMapInteraction(true);
  };

  const onRenderStartedHandler = (chart) => {
    onRenderStarted(chart);
    if (isEnabled) {
      setStartTime();
    }
  };

  const [countRetries, setCountRetries] = useState(0);

  const { start } = useCountdown({
    onTimeExpired: () => {
      selectionAbandoned('timeout');
      setCountRetries(countRetries + 1);
    },
  });

  useEffect(() => {
    if (countRetries <= HOLDTOKEN_LIMIT_TO_EXTEND_SECONDS) {
      changeExpirationHoldTokenAndSave(holdToken, HOLDTOKEN_LIFE_MINUTES);
      start(dayjs(), HOLDTOKEN_LIFE_SECONDS);
    }
  }, [countRetries]);

  useEffect(() => {
    createHoldTokenAndSave(HOLDTOKEN_LIFE_MINUTES);
    start(dayjs(), HOLDTOKEN_LIFE_SECONDS);
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        ...sx,
      }}
    >
      {isEnabled && !disabledSaturated ? (
        <Saturated />
      ) : (
        <Instructions show={!mapInteraction} />
      )}
      <CustomSelectionAlert />
      {isActiveRsvp && rsvp.type === 'hold' ? (
        <>
          {holdToken && (
            <SeatsioSeatingChart
              workspaceKey={process.env.NEXT_PUBLIC_SEATS_KEY}
              event={event.seats_event_key}
              holdToken={holdToken}
              region="eu"
              mode={
                isEnabled || eventStatus === 'pre-sale' ? 'static' : 'normal'
              }
              session="manual"
              availableCategories={availableCategories}
              pricing={categories}
              maxSelectedObjects={event.max_tickets_per_booking}
              language={currentLang.value}
              holdOnSelectForGAs
              onObjectSelected={onObjectSelected}
              onObjectDeselected={onObjectDeselected}
              onHoldSucceeded={onHoldHandler}
              onReleaseHoldSucceeded={onReleaseHoldSucceeded}
              onRenderStarted={onRenderStartedHandler}
              onObjectStatusChanged={onObjectStatusChanged}
              onHoldFailed={onHoldFailed}
              onReleaseHoldFailed={onReleaseHoldFailed}
              stylePreset="bubblegum"
              colors={{
                colorSelected: theme.palette.primary.main,
              }}
              priceFormatter={(price) => `${fCurrency(price)} ${currency}`}
              onHoldTokenExpired={onHoldTokenExpired}
              onObjectClicked={onObjectClickedHandler}
              isObjectVisible={(object, extraConfig) => {
                if (object.type === 'section') {
                  return true;
                }
                if (object.type === 'generalAdmission') {
                  return true;
                }
                if (object.type === 'seat') {
                  if (
                    extraConfig.seatsAvailables &&
                    extraConfig.seatsAvailables.includes(object.label)
                  ) {
                    return true;
                  }
                }
                return false;
              }}
              extraConfig={{ seatsAvailables }}
              selectableObjects={seatsAvailables}
            />
          )}
        </>
      ) : (
        <>
          {holdToken && (
            <SeatsioSeatingChart
              workspaceKey={process.env.NEXT_PUBLIC_SEATS_KEY}
              event={event.seats_event_key}
              holdToken={holdToken}
              region="eu"
              mode={
                isEnabled || eventStatus === 'pre-sale' ? 'static' : 'normal'
              }
              session="manual"
              availableCategories={availableCategories}
              pricing={categories}
              maxSelectedObjects={event.max_tickets_per_booking}
              language={currentLang.value}
              holdOnSelectForGAs
              onObjectSelected={onObjectSelected}
              onObjectDeselected={onObjectDeselected}
              onHoldSucceeded={onHoldHandler}
              onReleaseHoldSucceeded={onReleaseHoldSucceeded}
              onRenderStarted={onRenderStartedHandler}
              onObjectStatusChanged={onObjectStatusChanged}
              onHoldFailed={onHoldFailed}
              onReleaseHoldFailed={onReleaseHoldFailed}
              stylePreset="bubblegum"
              colors={{
                colorSelected: theme.palette.primary.main,
              }}
              priceFormatter={(price) => `${fCurrency(price)} ${currency}`}
              onHoldTokenExpired={onHoldTokenExpired}
              onObjectClicked={onObjectClickedHandler}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default CheckoutMap;
